import React from 'react'
import { Link } from 'gatsby'
import {
  Layout,
  Intro,
  Showcase,
  Header,
  SectionTitle,
  ItemTitle,
} from '../../../../components'

import './style.scss'
import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      />
      <Layout
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      >
        <Intro>
          <p>
            Notifications deliver{' '}
            <Link
              class="link"
              to={'/' + data.site + '/guidelines/feedback-errors/design'}
            >
              feedback and error
            </Link>{' '}
            messages to guide the user through a task. They can appear at the
            page level or inline next to specific page content. Text appears
            within a color-coded container to communicate the type of feedback,
            and the icon adds visual impact. A notification may include a{' '}
            <Link class="link" to={'/' + data.site + '/components/link/design'}>
              link
            </Link>{' '}
            to open a new page or a{' '}
            <Link class="link" to={'/' + data.site + '/patterns/modal/design'}>
              modal
            </Link>
            . Add a close icon to allow the user to dismiss a page-level
            notification, unless it's an error message.
          </p>
        </Intro>
        <SectionTitle>Variants</SectionTitle>

        <Showcase
          site={data.site}
          title="Info - Page Level"
          text="Use to present informational feedback at the page level."
          code={code.notificationInfo}
        >
          <div class="c-notification--info l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--info"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
              dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
              nec tincidunt velit quam ac sem.
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Info - Inline"
          text="Use to present informational feedback inline on the page."
          code={code.notificationInfo}
        >
          <div class="c-notification--inline-info l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--info"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </div>
          </div>
        </Showcase>

        <Showcase
          site={data.site}
          title="Warning - Page Level"
          text="Use to present warning feedback at the page level."
          code={code.notificationWarning}
        >
          <div class="c-notification--warning l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--exclamation"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
              dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
              nec tincidunt velit quam ac sem.
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Warning - Inline"
          text="Use to present warning feedback inline on the page."
          code={code.notificationWarning}
        >
          <div class="c-notification--inline-warning l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--exclamation"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Error - Page Level"
          text="	Use to present an error message at the page level."
          code={code.notificationWarning}
        >
          <div class="c-notification--error l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--error"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
              dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
              nec tincidunt velit quam ac sem.
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Error - Inline"
          text="		Use to present an error message inline on the page."
          code={code.notificationWarning}
        >
          <div class="c-notification--inline-error l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--error"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Success - Page Level"
          text="Use to present success feedback at the page level."
          code={code.notificationSuccess}
        >
          <div class="c-notification--success l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--success"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
              dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
              nec tincidunt velit quam ac sem.
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Success - Inline"
          text="Use to present success feedback inline on the page."
          code={code.notificationSuccess}
        >
          <div class="c-notification--inline-success l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--success"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </div>
          </div>
        </Showcase>

        <SectionTitle>Design Environment</SectionTitle>
        <ItemTitle>Elevation</ItemTitle>
        <p>
          Inline notifications will push all content below them down rather than
          appearing elevated above it. Page-level notifications can appear
          either embedded at the top of the page, or elevated on the{' '}
          <Link
            class="link"
            to={'/' + data.site + '/guidelines/elevation/design'}
          >
            attention pane
          </Link>{' '}
          to allow page content to scroll behind. When elevated, the
          notification will use a medium{' '}
          <Link class="link" to={'/' + data.site + '/utilities/styles/design'}>
            shadow
          </Link>
          .
        </p>
        <p>
          Generally, you should elevate a notification with information that the
          user will need to remain aware of as they navigate the page content,
          for example, a page-level error that can be resolved from two or more
          parts of the page. Notifications that contain information that is not
          pertinent to the user's task, for example a page-level information
          notification of an upcoming system outage, should be embedded within
          the layout so as not to distract the user from the page content
          they're interacting with.
        </p>
        <p>
          Notification height should preserve a 16px padding around the text
          within the component.
        </p>
        {/* <table class="l-flex-col--xs-8">
          <thead>
            <tr>
              <th>Number of Lines</th>
              <th>Notification Height</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>46px</td>
            </tr>
            <tr>
              <td>2</td>
              <td>65px</td>
            </tr>{' '}
            <tr>
              <td>3</td>
              <td>82px</td>
            </tr>{' '}
            <tr>
              <td>4</td>
              <td>99px</td>
            </tr>
          </tbody>
        </table> */}

        <SectionTitle>Microcopy</SectionTitle>
        <p>
          Use full sentences but keep notifications as brief and to-the-point as
          possible. Make sure that the user understands what is required of them
          to continue. To prevent excessive shifting of page content, try to
          keep inline notifications to two lines of text or less at your
          smallest breakpoint.
        </p>
      </Layout>
    </>
  )
}

export const code = {
  /*
  notificationInfo: `<div class="c-notification--info">
  <div class="c-notification__content">Content</div>
</div>
`,
  notificationSuccess: `<div class="c-notification--success">
  <div class="c-notification__content">Content</div>
</div>
`,
  notificationWarning: `<div class="c-notification--warning">
  <div class="c-notification__content">Content</div>
</div>
`,
  notificationError: `<div class="c-notification--error">
  <div class="c-notification__content">Content</div>
</div>
`,
  iconNotifications: `<div class="c-notification--info">
  <div class="c-notification__icon"><Icon/></div>
  <div class="c-notification__content">Content</div>
</div>
`,*/
}
